import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { Upload, CheckCircle, XCircle, Calendar, FileText, Trash2, RefreshCw, Download, Clock, AlertCircle, Loader2 } from "lucide-react";
import { adminApi } from "../../utils/adminApi";
import { auth } from "../../lib/firebase";

interface RocketDownloadJob {
  id: string;
  status: 'pending' | 'in_progress' | 'waiting_2fa' | 'completed' | 'failed';
  triggered_by: string;
  attempt_count: number;
  max_attempts: number;
  tfa_requested_at: string | null;
  error_message: string | null;
  created_at: string;
}

interface RateSheet {
  id: string;
  effective_date: string;
  status: string;
  file_name: string;
  uploaded_by: string;
  created_at: string;
  stats: {
    base_rates: number;
    llpa_adjustments: number;
    loan_ladder_adjustments: number;
    promotions: number;
  };
}

const RateSheetManagement: React.FC = () => {
  const queryClient = useQueryClient();
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);

  // Fetch current rate sheet
  const { data: currentSheet, isLoading: isLoadingCurrent } = useQuery<RateSheet>(
    "currentRateSheet",
    async () => {
      return adminApi.get("/rate-sheets/current");
    },
    {
      retry: false,
      refetchOnWindowFocus: false,
      onError: (error) => {
        // 404 is expected when no active rate sheet exists
        console.log("No active rate sheet found (expected on first load)");
      },
    }
  );

  // Fetch all rate sheets
  const { data: allSheets, isLoading: isLoadingAll } = useQuery<{
    rateSheets: RateSheet[];
    pagination: { page: number; limit: number; total: number; pages: number };
  }>(
    "allRateSheets",
    async () => {
      return adminApi.get("/rate-sheets?page=1&limit=10");
    },
    {
      retry: false,
      refetchOnWindowFocus: false,
      onError: (error) => {
        console.error("Error fetching rate sheets:", error);
      },
    }
  );

  // Upload mutation
  const uploadMutation = useMutation(
    async (file: File) => {
      const formData = new FormData();
      formData.append("rateSheet", file);

      const user = auth.currentUser;
      if (user) {
        formData.append("uploadedBy", user.email || "admin");
      }

      const token = await user?.getIdToken();
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

      const response = await fetch(`${API_BASE_URL}/admin/rate-sheets/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Upload failed");
      }

      return response.json();
    },
    {
      onSuccess: (data) => {
        setUploadSuccess(`Rate sheet uploaded successfully! ${data.stats.base_rates} rates loaded.`);
        setUploadFile(null);
        setUploadError(null);
        queryClient.invalidateQueries("currentRateSheet");
        queryClient.invalidateQueries("allRateSheets");
      },
      onError: (error: Error) => {
        setUploadError(error.message);
        setUploadSuccess(null);
      },
    }
  );

  // Activate mutation
  const activateMutation = useMutation(
    async (id: string) => {
      return adminApi.post(`/rate-sheets/${id}/activate`, {});
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("currentRateSheet");
        queryClient.invalidateQueries("allRateSheets");
      },
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    async (id: string) => {
      return adminApi.delete(`/rate-sheets/${id}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("allRateSheets");
      },
    }
  );

  // Fetch current download job
  const { data: currentJob, refetch: refetchJob } = useQuery<{ job: RocketDownloadJob | null }>(
    "currentDownloadJob",
    async () => {
      return adminApi.get("/rocket-download/jobs/current");
    },
    {
      refetchInterval: (data) => {
        // Poll every 5 seconds if there's an active job
        const job = data?.job;
        if (job && ['pending', 'in_progress', 'waiting_2fa'].includes(job.status)) {
          return 5000;
        }
        return false;
      },
      refetchOnWindowFocus: true,
    }
  );

  // State for 2FA code input
  const [tfaCode, setTfaCode] = useState("");
  const [tfaError, setTfaError] = useState<string | null>(null);

  // Create download job mutation
  const downloadMutation = useMutation(
    async () => {
      return adminApi.post("/rocket-download/jobs", { triggered_by: "admin_ui" });
    },
    {
      onSuccess: () => {
        refetchJob();
        queryClient.invalidateQueries("allRateSheets");
      },
    }
  );

  // Submit 2FA code mutation
  const submit2faMutation = useMutation(
    async ({ jobId, code }: { jobId: string; code: string }) => {
      return adminApi.post(`/rocket-download/jobs/${jobId}/submit-2fa`, { code });
    },
    {
      onSuccess: () => {
        setTfaCode("");
        setTfaError(null);
        refetchJob();
      },
      onError: (error: Error) => {
        setTfaError(error.message);
      },
    }
  );

  // Cancel job mutation
  const cancelJobMutation = useMutation(
    async (jobId: string) => {
      return adminApi.post(`/rocket-download/jobs/${jobId}/cancel`, {});
    },
    {
      onSuccess: () => {
        refetchJob();
      },
    }
  );

  const handleCancelJob = () => {
    if (currentJob?.job?.id) {
      cancelJobMutation.mutate(currentJob.job.id);
    }
  };

  const handleDownloadFromRocket = () => {
    downloadMutation.mutate();
  };

  const handleSubmit2fa = () => {
    if (currentJob?.job?.id && tfaCode) {
      submit2faMutation.mutate({ jobId: currentJob.job.id, code: tfaCode });
    }
  };

  const getJobStatusDisplay = (job: RocketDownloadJob) => {
    switch (job.status) {
      case 'pending':
        return { icon: Clock, text: 'Waiting for download script...', color: 'text-yellow-600', bg: 'bg-yellow-50' };
      case 'in_progress':
        return { icon: Loader2, text: 'Downloading from Rocket Pro...', color: 'text-blue-600', bg: 'bg-blue-50', spin: true };
      case 'waiting_2fa':
        return { icon: AlertCircle, text: '2FA code needed', color: 'text-orange-600', bg: 'bg-orange-50' };
      case 'failed':
        return { icon: XCircle, text: `Failed: ${job.error_message || 'Unknown error'}`, color: 'text-red-600', bg: 'bg-red-50' };
      case 'completed':
        return { icon: CheckCircle, text: 'Download completed', color: 'text-green-600', bg: 'bg-green-50' };
      default:
        return { icon: Clock, text: job.status, color: 'text-gray-600', bg: 'bg-gray-50' };
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadFile(file);
      setUploadError(null);
      setUploadSuccess(null);
    }
  };

  const handleUpload = () => {
    if (uploadFile) {
      uploadMutation.mutate(uploadFile);
    }
  };

  const handleActivate = (id: string) => {
    if (confirm("Activate this rate sheet? The current sheet will be deactivated.")) {
      activateMutation.mutate(id);
    }
  };

  const handleDelete = (id: string) => {
    if (confirm("Delete this rate sheet? This action cannot be undone.")) {
      deleteMutation.mutate(id);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="space-y-8">
      {/* Download from Rocket Section */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-heading font-semibold text-gray-900">
            Download from Rocket Pro
          </h2>
        </div>
        <div className="p-6">
          {currentJob?.job && ['pending', 'in_progress', 'waiting_2fa'].includes(currentJob.job.status) ? (
            <div className="space-y-4">
              {/* Job Status Display */}
              {(() => {
                const status = getJobStatusDisplay(currentJob.job);
                const IconComponent = status.icon;
                return (
                  <div className={`flex items-center justify-between p-4 rounded-md ${status.bg}`}>
                    <div className="flex items-center space-x-3">
                      <IconComponent
                        size={20}
                        className={`${status.color} ${status.spin ? 'animate-spin' : ''}`}
                      />
                      <div>
                        <span className={`text-sm font-medium ${status.color}`}>
                          {status.text}
                        </span>
                        <p className="text-xs text-gray-500 mt-1">
                          Attempt {currentJob.job.attempt_count} of {currentJob.job.max_attempts}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleCancelJob}
                      disabled={cancelJobMutation.isLoading}
                      className="text-sm text-gray-500 hover:text-red-600 transition-colors"
                    >
                      {cancelJobMutation.isLoading ? 'Cancelling...' : 'Cancel'}
                    </button>
                  </div>
                );
              })()}

              {/* 2FA Input */}
              {currentJob.job.status === 'waiting_2fa' && (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    Check your phone for the 2FA code from Rocket Pro and enter it below:
                  </p>
                  <div className="flex space-x-3">
                    <input
                      type="text"
                      value={tfaCode}
                      onChange={(e) => setTfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Enter 6-digit code"
                      maxLength={6}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-primary text-lg tracking-widest font-mono"
                    />
                    <button
                      onClick={handleSubmit2fa}
                      disabled={tfaCode.length !== 6 || submit2faMutation.isLoading}
                      className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submit2faMutation.isLoading ? 'Submitting...' : 'Submit'}
                    </button>
                  </div>
                  {tfaError && (
                    <p className="text-sm text-red-600">{tfaError}</p>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">
                  Trigger automated download from Rocket Pro portal.
                  Your MacBook will handle the login and download.
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Make sure the download script is running on your MacBook.
                </p>
              </div>
              <button
                onClick={handleDownloadFromRocket}
                disabled={downloadMutation.isLoading}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {downloadMutation.isLoading ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Creating job...
                  </>
                ) : (
                  <>
                    <Download size={16} className="mr-2" />
                    Download from Rocket
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Upload Section */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-heading font-semibold text-gray-900">
            Upload New Rate Sheet
          </h2>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Excel File (.xlsx)
              </label>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-semibold
                  file:bg-primary file:text-white
                  hover:file:bg-primary-dark
                  file:cursor-pointer cursor-pointer"
              />
            </div>

            {uploadFile && (
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-md">
                <div className="flex items-center space-x-2">
                  <FileText size={20} className="text-gray-600" />
                  <span className="text-sm text-gray-700">{uploadFile.name}</span>
                </div>
                <button
                  onClick={handleUpload}
                  disabled={uploadMutation.isLoading}
                  className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploadMutation.isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload size={16} className="mr-2" />
                      Upload
                    </>
                  )}
                </button>
              </div>
            )}

            {uploadError && (
              <div className="flex items-start space-x-2 p-4 bg-red-50 border border-red-200 rounded-md">
                <XCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-700">{uploadError}</div>
              </div>
            )}

            {uploadSuccess && (
              <div className="flex items-start space-x-2 p-4 bg-green-50 border border-green-200 rounded-md">
                <CheckCircle size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-green-700">{uploadSuccess}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Current Rate Sheet */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-heading font-semibold text-gray-900">
            Current Active Rate Sheet
          </h2>
        </div>
        <div className="p-6">
          {isLoadingCurrent ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : !currentSheet ? (
            <div className="text-center py-8 text-gray-500">
              No active rate sheet. Upload one to enable automated pricing.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-4">Details</h3>
                <dl className="space-y-2">
                  <div className="flex items-center">
                    <dt className="text-sm text-gray-600 w-32">File Name:</dt>
                    <dd className="text-sm font-medium text-gray-900">{currentSheet.file_name}</dd>
                  </div>
                  <div className="flex items-center">
                    <dt className="text-sm text-gray-600 w-32">Effective Date:</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {formatDate(currentSheet.effective_date)}
                    </dd>
                  </div>
                  <div className="flex items-center">
                    <dt className="text-sm text-gray-600 w-32">Uploaded:</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {formatDate(currentSheet.created_at)}
                    </dd>
                  </div>
                  <div className="flex items-center">
                    <dt className="text-sm text-gray-600 w-32">Uploaded By:</dt>
                    <dd className="text-sm font-medium text-gray-900">{currentSheet.uploaded_by}</dd>
                  </div>
                </dl>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-4">Statistics</h3>
                <dl className="space-y-2">
                  <div className="flex items-center">
                    <dt className="text-sm text-gray-600 w-40">Base Rates:</dt>
                    <dd className="text-sm font-medium text-gray-900">{currentSheet.stats.base_rates}</dd>
                  </div>
                  <div className="flex items-center">
                    <dt className="text-sm text-gray-600 w-40">LLPA Adjustments:</dt>
                    <dd className="text-sm font-medium text-gray-900">{currentSheet.stats.llpa_adjustments}</dd>
                  </div>
                  <div className="flex items-center">
                    <dt className="text-sm text-gray-600 w-40">Loan Ladder:</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {currentSheet.stats.loan_ladder_adjustments}
                    </dd>
                  </div>
                  <div className="flex items-center">
                    <dt className="text-sm text-gray-600 w-40">Promotions:</dt>
                    <dd className="text-sm font-medium text-gray-900">{currentSheet.stats.promotions}</dd>
                  </div>
                </dl>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* All Rate Sheets */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-heading font-semibold text-gray-900">
            All Rate Sheets
          </h2>
        </div>
        <div className="p-6">
          {isLoadingAll ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : !allSheets?.rateSheets?.length ? (
            <div className="text-center py-8 text-gray-500">
              No rate sheets found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                      Status
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                      File Name
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                      Effective Date
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                      Uploaded
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                      Stats
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {allSheets.rateSheets.map((sheet) => (
                    <tr
                      key={sheet.id}
                      className="border-b border-gray-100 hover:bg-gray-50"
                    >
                      <td className="py-3 px-4">
                        {sheet.status === "active" ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle size={12} className="mr-1" />
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-gray-700 text-sm">
                        {sheet.file_name}
                      </td>
                      <td className="py-3 px-4 text-gray-700 text-sm">
                        {formatDate(sheet.effective_date)}
                      </td>
                      <td className="py-3 px-4 text-gray-700 text-sm">
                        {formatDate(sheet.created_at)}
                      </td>
                      <td className="py-3 px-4 text-gray-700 text-sm">
                        {sheet.stats?.base_rates || 0} rates
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center space-x-2">
                          {sheet.status !== "active" && (
                            <button
                              onClick={() => handleActivate(sheet.id)}
                              disabled={activateMutation.isLoading}
                              className="inline-flex items-center text-primary hover:text-primary-dark transition-colors disabled:opacity-50"
                              title="Activate"
                            >
                              <RefreshCw size={16} />
                            </button>
                          )}
                          {sheet.status !== "active" && (
                            <button
                              onClick={() => handleDelete(sheet.id)}
                              disabled={deleteMutation.isLoading}
                              className="inline-flex items-center text-red-600 hover:text-red-700 transition-colors disabled:opacity-50"
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RateSheetManagement;
